import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './AdminNav.css';

const NAV = [
  { label: 'Dashboard', path: '/admin/dashboard' },
  {
    label: 'Hub',
    children: [
      { label: 'Office / Oversight', path: '/admin/hub/oversight' },
      { label: 'Cleaning', path: '/admin/hub/cleaning' },
      { label: 'Hospitality', path: '/admin/hub/hospitality' },
    ]
  },
  {
    label: 'Volunteers',
    children: [
      { label: 'Roster', path: '/admin/roster' },
      { label: 'Availability', path: '/admin/availability' },
      { label: 'Broadcast Email', path: '/admin/broadcast' },
    ]
  },
  {
    label: 'Scheduling',
    children: [
      { label: 'Reference Sheet', path: '/admin/cheatsheet' },
      {
        label: 'Carts',
        children: [
          { label: 'Shifts', path: '/admin/schedule' },
          { label: 'Volunteer Load', path: '/admin/volunteer-load' },
          { label: 'Confirmation', path: '/admin/confirmation' },
          { label: 'Scheduling Settings', path: '/admin/scheduling-settings' },
        ]
      },
      {
        label: 'Shuttling / Drivers',
        children: [
          { label: 'Drivers', path: '/admin/drivers' },
          { label: 'Parking Attendants', path: '/admin/parking' },
        ]
      },
    ]
  },
  {
    label: 'Masters Event',
    children: [
      { label: 'Check-In', path: '/admin/checkin' },
      { label: 'Day-Of', path: '/admin/dayof' },
    ]
  },
  { label: 'Settings', path: '/admin/settings' },
];

// Returns array of group labels that are ancestors of the given path
function getAncestorLabels(items, targetPath, ancestors = []) {
  for (const item of items) {
    if (item.path === targetPath) return ancestors;
    if (item.children) {
      const result = getAncestorLabels(item.children, targetPath, [...ancestors, item.label]);
      if (result !== null) return result;
    }
  }
  return null;
}

function NavItem({ item, expanded, onToggle, sidebarOpen, currentPath, depth = 0, onNavigate }) {
  if (item.path) {
    const isActive = currentPath === item.path;
    return (
      <Link
        to={item.path}
        className={`nav-leaf${isActive ? ' active' : ''}`}
        style={{ paddingLeft: `${16 + depth * 16}px` }}
        title={!sidebarOpen ? item.label : undefined}
        onClick={onNavigate}
      >
        <span className="nav-label">{item.label}</span>
      </Link>
    );
  }

  const isOpen = expanded.has(item.label);
  return (
    <div className="nav-group">
      <button
        className={`nav-group-header${isOpen ? ' open' : ''}`}
        style={{ paddingLeft: `${16 + depth * 16}px` }}
        onClick={() => onToggle(item.label)}
        title={!sidebarOpen ? item.label : undefined}
      >
        <span className="nav-label">{item.label}</span>
        {sidebarOpen && <span className="nav-chevron">{isOpen ? '▾' : '▸'}</span>}
      </button>
      {isOpen && sidebarOpen && (
        <div className="nav-children">
          {item.children.map(child => (
            <NavItem
              key={child.label}
              item={child}
              expanded={expanded}
              onToggle={onToggle}
              sidebarOpen={sidebarOpen}
              currentPath={currentPath}
              depth={depth + 1}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function AdminNav({ onLogout }) {
  const location = useLocation();
  const currentPath = location.pathname;

  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (window.innerWidth <= 640) return false; // always start closed on mobile
    return localStorage.getItem('sidebarOpen') !== 'false';
  });

  const [expanded, setExpanded] = useState(() => {
    const ancestors = getAncestorLabels(NAV, currentPath) || [];
    const saved = JSON.parse(localStorage.getItem('navExpanded') || '[]');
    return new Set([...ancestors, ...saved]);
  });

  // Keep CSS variable in sync so admin pages know sidebar width
  useEffect(() => {
    document.documentElement.style.setProperty('--sidebar-width', sidebarOpen ? '220px' : '52px');
    localStorage.setItem('sidebarOpen', String(sidebarOpen));
  }, [sidebarOpen]);

  // Auto-expand ancestor groups when navigating to a new route
  useEffect(() => {
    const ancestors = getAncestorLabels(NAV, currentPath) || [];
    if (ancestors.length > 0) {
      setExpanded(prev => {
        const next = new Set(prev);
        ancestors.forEach(a => next.add(a));
        return next;
      });
    }
  }, [currentPath]);

  useEffect(() => {
    localStorage.setItem('navExpanded', JSON.stringify([...expanded]));
  }, [expanded]);

  const toggleGroup = (label) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  return (
    <>
      <aside className={`admin-sidebar${sidebarOpen ? '' : ' collapsed'}`}>
        <div className="sidebar-header">
          <button
            className="sidebar-toggle"
            onClick={() => setSidebarOpen(o => !o)}
            title={sidebarOpen ? 'Collapse menu' : 'Expand menu'}
            aria-label="Toggle menu"
          >
            ☰
          </button>
          {sidebarOpen && <span className="sidebar-title">Admin</span>}
          <span className="sidebar-title-mobile">SMPW Masters</span>
        </div>

        <nav className="sidebar-nav">
          {NAV.map(item => (
            <NavItem
              key={item.label}
              item={item}
              expanded={expanded}
              onToggle={toggleGroup}
              sidebarOpen={sidebarOpen}
              currentPath={currentPath}
              onNavigate={() => setSidebarOpen(false)}
            />
          ))}
        </nav>

        <div className="sidebar-footer">
          <button onClick={onLogout} className="sidebar-logout" title="Logout">
            {sidebarOpen ? 'Logout' : '⏻'}
          </button>
        </div>
      </aside>

      {sidebarOpen && (
        <div className="sidebar-backdrop" onClick={() => setSidebarOpen(false)} />
      )}
    </>
  );
}

export default AdminNav;
